import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {SendabilityStatus} from 'nutshell-graphql-types';
import type {
    GetChannelEvents as GetChannelEventsQuery,
    GetChannelEventsVariables as GetChannelEventsQueryVariables,
    InboxChannelEventFragment,
} from 'nutshell-graphql-types';

import {getChannelEventsForTimeline} from '../../helpers';
import {useGetScheduledMessages} from '../../sms/graphql/hooks/use-get-scheduled-messages';

import GET_CHANNEL_EVENTS from '../queries/get-channel-events.graphql';

const LIMIT = 40;
const POLL_INTERVAL_MILLISECONDS = 5000; // 5 seconds

export const useGetChannelEvents = (
    channelId?: string | null,
    threadId?: string | null
): {
    sendabilityStatus: SendabilityStatus | null | undefined;
    channelEvents: any[];
    error: string | null | undefined;
    isLoading: boolean;
    hasNextPage: boolean;
    lastSeenTime: string | null | undefined;
    isActive: boolean;
    fetchMoreRows: () => Promise<any>;
    fetchMostRecentChannelEvents: () => Promise<any>;
    refetchScheduledChannelEvents: () => Promise<any>;
} => {
    const [currentStartCursor, setCurrentStartCursor] = React.useState<string | null | undefined>(
        null
    );
    const [intervalId, setIntervalId] = React.useState<any | null | undefined>(null);
    const {
        data,
        error,
        loading: isLoadingChannelEvents,
        fetchMore,
        networkStatus,
    } = useQuery<GetChannelEventsQuery, GetChannelEventsQueryVariables>(GET_CHANNEL_EVENTS, {
        skip: !channelId,
        // @ts-expect-error
        variables: {first: LIMIT, channelId, threadId},
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const eventsData = data ? data.channelEvents : null;

    const events: InboxChannelEventFragment[] = eventsData
        ? eventsData.edges.map((edge) => edge.node)
        : [];

    const channelEvents = getChannelEventsForTimeline(events);
    const isFirstLoad = networkStatus === 1;
    const pageInfo = eventsData ? eventsData.pageInfo : {};
    // @ts-expect-error
    const {startCursor, endCursor} = pageInfo;
    const lastSeenTime = eventsData ? eventsData.lastSeenTime : null;
    const isActive = eventsData ? eventsData.isActive : null;

    React.useEffect(() => {
        if (isFirstLoad) {
            setCurrentStartCursor(null);
        } else if (!isLoadingChannelEvents) {
            setCurrentStartCursor(startCursor);
        }
    }, [startCursor, isLoadingChannelEvents, isFirstLoad]);

    // Clear the interval when the component unmounts
    React.useEffect(() => {
        if (channelId) {
            return () => clearInterval(intervalId);
        }
    }, [intervalId, channelId]);

    // Clear the start cursor if the requested channelId or threadId changes
    React.useEffect(() => {
        setCurrentStartCursor(null);
    }, [channelId, threadId]);

    // polling for new events using the most up-to-date startCursor
    React.useEffect(() => {
        if (intervalId) {
            clearInterval(intervalId);
        }

        if (currentStartCursor) {
            const tick = () => {
                if (currentStartCursor) {
                    fetchMostRecentChannelEvents(currentStartCursor);
                }
            };

            const id = setInterval(tick, POLL_INTERVAL_MILLISECONDS);
            setIntervalId(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStartCursor, channelId, threadId]);

    // Also grab scheduled messages and return them at the end of the list
    const {
        scheduledMessages,
        isLoading: isLoadingScheduledMessages,
        refetch: refetchScheduledChannelEvents,
    } = useGetScheduledMessages(channelId, threadId);

    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: LIMIT,
                after: endCursor,
            },
            updateQuery: (prev, {fetchMoreResult}) => {
                if (!fetchMoreResult) {
                    return prev;
                }

                // @ts-expect-error
                const oldEvents = prev.channelEvents;
                const oldEdges = oldEvents ? oldEvents.edges : [];
                // @ts-expect-error
                const newEvents = fetchMoreResult.channelEvents;

                return {
                    channelEvents: {
                        // @ts-expect-error
                        ...fetchMoreResult.channelEvents,
                        edges: oldEdges.concat(newEvents.edges),
                    },
                };
            },
        });
    };

    // Called when polling - gets any new events and puts them at the bottom of the channel events list
    const fetchMostRecentChannelEvents = (beforeCursor?: string | null) => {
        if (!beforeCursor) {
            // no-op if no beforeCursor is passed
            return new Promise((resolve) => {
                // @ts-expect-error
                resolve();
            });
        }

        // Stop polling to prevent multiple fetches from happening at the same time
        setCurrentStartCursor(null);

        return fetchMore({
            variables: {
                first: undefined, // must be undefined to get the most recent events without erroring
                before: beforeCursor,
            },
            // @ts-expect-error
            updateQuery: (prev, {fetchMoreResult}) => {
                const oldEvents = prev ? prev.channelEvents : {};
                // @ts-expect-error
                const oldEdges = oldEvents && oldEvents.edges ? oldEvents.edges : [];
                // @ts-expect-error
                const mostRecentEvents = fetchMoreResult.channelEvents;
                const mostRecentEdges = mostRecentEvents.edges;
                if (
                    mostRecentEdges.length === 0 &&
                    // @ts-expect-error
                    mostRecentEvents.lastSeenTime === oldEvents.lastSeenTime &&
                    // @ts-expect-error
                    mostRecentEvents.isActive === oldEvents.isActive
                ) {
                    return prev;
                }

                // Filter out any events that are already in the list. This is necessary because
                // we might be polling for new events at the same time this runs as a refetch after
                // sending a message and we don't want to show duplicates
                const newEvents = {
                    ...oldEvents,
                    edges: filterOutDuplicateEvents(oldEdges, mostRecentEdges).concat(oldEdges),
                    sendabilityStatus: mostRecentEvents.sendabilityStatus,
                    lastSeenTime: mostRecentEvents.lastSeenTime,
                    isActive: mostRecentEvents.isActive,
                } as const;

                // Update the startCursor if it exists if no new events the start cursor will be null
                if (mostRecentEvents.pageInfo.startCursor) {
                    setCurrentStartCursor(mostRecentEvents.pageInfo.startCursor);
                }

                return {channelEvents: newEvents};
            },
        });
    };

    return {
        sendabilityStatus: eventsData && eventsData.sendabilityStatus,
        channelEvents: channelEvents.concat(scheduledMessages),
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading: isLoadingChannelEvents || isLoadingScheduledMessages,
        // @ts-expect-error
        hasNextPage: Boolean(pageInfo && pageInfo.hasNextPage),
        lastSeenTime,
        isActive: isActive || false,
        fetchMoreRows,
        fetchMostRecentChannelEvents: () => fetchMostRecentChannelEvents(currentStartCursor),
        refetchScheduledChannelEvents,
    };
};

// @ts-expect-error
const filterOutDuplicateEvents = (oldEvents, newEvents) => {
    return newEvents.filter(
        // @ts-expect-error
        (event) =>
            // @ts-expect-error
            !oldEvents.find((oldEvent) => {
                return (
                    oldEvent.node.payload.id === event.node.payload.id &&
                    oldEvent.node.changeTime === event.node.changeTime
                );
            })
    );
};
